import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import TierSelector from '../../components/funnel/TierSelector';
import AddOnSelector from '../../components/funnel/AddOnSelector';
import CartSummary from '../../components/funnel/CartSummary';
import FunnelShell from './FunnelShell';
import ManagedMarketingServicePage from './ManagedMarketingServicePage';
import PlatformDevelopmentServicePage from './PlatformDevelopmentServicePage';
import AssetsDesignServicePage from './AssetsDesignServicePage';
import CustomProjectsServicePage from './CustomProjectsServicePage';
import { useFunnelCart } from '../../context/FunnelCartProvider';
import {
  getServiceBySlug,
  getSkuById,
  MARKETING_TIER_TO_ONBOARDING_SKU,
} from '../../lib/pricebook/pricebook';
import { usePricebookCatalogSync } from '../../hooks/usePricebookCatalogSync';

const Layout = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr) 320px;

  @media (max-width: 980px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Main = styled.section`
  display: grid;
  gap: 1rem;
`;

const Hero = styled.header`
  display: grid;
  gap: 0.5rem;
`;

const InlineActions = styled.div`
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const PrimaryLink = styled(Link)`
  text-decoration: none;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  font-weight: 700;
`;

const PrimaryAnchor = styled.a`
  text-decoration: none;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  font-weight: 700;
`;

const GhostLink = styled(Link)`
  text-decoration: none;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  color: ${({ theme }) => theme.colors.gray900};
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  font-weight: 700;
`;

export default function ServiceSelectionPage() {
  usePricebookCatalogSync();
  const { serviceSlug = '' } = useParams<{ serviceSlug: string }>();
  const service = getServiceBySlug(serviceSlug);
  const { items, totalQuantity, selectPrimaryTier, openDrawer, primaryCategories } =
    useFunnelCart();

  if (!service) {
    return (
      <FunnelShell>
        <h1>Service not found</h1>
        <p>The selected service page does not exist.</p>
      </FunnelShell>
    );
  }

  if (service.category === 'marketing') {
    return <ManagedMarketingServicePage service={service} />;
  }

  if (service.category === 'platform') {
    return <PlatformDevelopmentServicePage service={service} />;
  }

  if (service.category === 'assets_design') {
    return <AssetsDesignServicePage service={service} />;
  }

  if (service.category === 'custom') {
    return <CustomProjectsServicePage service={service} />;
  }

  const selectedTierSkuId =
    items.find((item) => {
      const sku = getSkuById(item.skuId);
      return sku?.serviceCategory === service.category && sku.isPrimaryService;
    })?.skuId ?? null;

  const includedAddOnSkuIds = selectedTierSkuId
    ? [MARKETING_TIER_TO_ONBOARDING_SKU[selectedTierSkuId]].filter(Boolean)
    : [];

  const showQuoteCTA = service.category === 'custom';

  return (
    <FunnelShell>
      <Layout>
        <Main>
          <Hero>
            <h1 style={{ margin: 0 }}>{service.title}</h1>
            <p style={{ margin: 0 }}>{service.description}</p>
            <p style={{ margin: 0, fontWeight: 700, color: '#1d4ed8' }}>
              {service.teaser}
            </p>
          </Hero>

          {showQuoteCTA ? (
            <InlineActions>
              <PrimaryAnchor href="mailto:hello@cybard.co?subject=CyBard%20Custom%20Project%20Quote">
                Request a quote
              </PrimaryAnchor>
              <GhostLink to="/services">Back to services</GhostLink>
            </InlineActions>
          ) : (
            <>
              <TierSelector
                tiers={service.tiers}
                selectedSkuId={selectedTierSkuId}
                onSelect={selectPrimaryTier}
              />
              <AddOnSelector
                addOns={service.addOns}
                includedSkuIds={includedAddOnSkuIds}
              />
              <InlineActions>
                <PrimaryLink to="/checkout">Continue</PrimaryLink>
                <GhostLink to="/checkout">Go to checkout</GhostLink>
              </InlineActions>
            </>
          )}
        </Main>
        <CartSummary
          itemCount={totalQuantity}
          primaryServiceCount={primaryCategories.length}
          onOpenCart={openDrawer}
        />
      </Layout>
    </FunnelShell>
  );
}
